// Live contract for GET/PUT /api/ai/*. Nullable fields are omitted from the JSON when null.

export type AiRecommendationType = 'BID' | 'PACING' | 'YIELD_FLOOR' | 'REVENUE_OPPORTUNITY' | 'CREATIVE_ROTATION' | 'TARGETING';
export type AiRecommendationStatus = 'GENERATED' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'APPLIED' | 'EXPIRED' | 'SUPERSEDED';
export type AiAnomalyCategory = 'TRAFFIC' | 'FILL' | 'CTR' | 'COMPLETION' | 'REVENUE' | 'LATENCY' | 'PACING_UNDER' | 'PACING_OVER' | 'DATA_DELAY';
export type AiAnomalyStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'IGNORED' | 'FALSE_POSITIVE';
export type AiForecastType = 'CAMPAIGN_SPEND' | 'CAMPAIGN_DELIVERY' | 'PUBLISHER_REVENUE' | 'INVENTORY_DEMAND';
export type AiTargetType = 'LINE_ITEM' | 'CREATIVE' | 'PUBLISHER' | 'PLACEMENT' | 'DEMAND_CONNECTION' | 'PLATFORM';
export type AiSeverity = 'LOW' | 'MEDIUM' | 'HIGH';
export type AiConfidence = 'LOW' | 'MEDIUM' | 'HIGH';
export type AiSource = 'DXK' | 'EXTERNAL';

export type AiInsightKind = 'recommendations' | 'anomalies' | 'yield' | 'forecasts';

export type AiInsightsPreview = 'ready' | 'loading' | 'empty' | 'off' | 'error' | 'stale';

export const AI_INSIGHTS_PREVIEWS: AiInsightsPreview[] = ['ready', 'loading', 'empty', 'off', 'error', 'stale'];

export interface AiRecommendation {
  id: string;
  type: AiRecommendationType;
  targetType: AiTargetType;
  targetId: string;
  /** Entity name filled by the backend (B5); absent for platform-wide or deleted targets. */
  targetName?: string;
  publisherId?: string;
  placementId?: string;
  inventoryType?: string;
  currentValue?: number;
  suggestedValue?: number;
  rationale: string;
  supportingMetrics?: Record<string, unknown>;
  confidence?: AiConfidence;
  dataWindowStart: string;
  dataWindowEnd: string;
  ruleVersion: string;
  source: AiSource;
  status: AiRecommendationStatus;
  expiresAt?: string;
  createdOn: string;
  updatedOn?: string;
  modifiedBy?: string;
}

/** One status change. ANOM-03a omits the list when it has not recorded history yet. */
export interface AiAnomalyStatusEvent {
  status: AiAnomalyStatus;
  changedAt: string;
  changedBy?: string;
  note?: string;
}

/** AI-C2: platform-wide latency versus one demand partner. */
export type AiLatencyScope = 'PLATFORM' | 'PARTNER';

export interface AiAnomaly {
  id: string;
  category: AiAnomalyCategory;
  targetType: AiTargetType;
  targetId?: string;
  /** Entity name filled by the backend (B5); absent for platform-wide or deleted targets. */
  targetName?: string;
  metric: string;
  detectedAt: string;
  severity: AiSeverity;
  observedValue?: number;
  expectedLow?: number;
  expectedHigh?: number;
  deviationPercent?: number;
  explanation: string;
  ruleVersion: string;
  status: AiAnomalyStatus;
  notificationSentAt?: string;
  createdOn: string;
  updatedOn?: string;
  /** Who last changed the status. */
  modifiedBy?: string;
  /** Length of this incident. A continuing anomaly is one row, not one row per hour. */
  durationMinutes?: number;
  /** Last hour this same incident was still detected. */
  lastSeenAt?: string;
  peakValue?: number;
  peakAt?: string;
  latencyScope?: AiLatencyScope;
  history?: AiAnomalyStatusEvent[];
}

export interface AiForecast {
  id: string;
  type: AiForecastType;
  targetType: AiTargetType;
  targetId: string;
  horizonStart: string;
  horizonEnd: string;
  predictedValue: number;
  predictedLow?: number;
  predictedHigh?: number;
  explanation: string;
  confidence?: AiConfidence;
  ruleVersion: string;
  dataAsOf: string;
  actualValue?: number;
  actualRecordedAt?: string;
  createdOn: string;
}

export interface AiSummary {
  openHighSeverityAnomalies: number;
  pendingRecommendations: number;
  /** Present once ANOM-03a breaks the open count down. Absent on the older summary. */
  openAnomaliesBySeverity?: Partial<Record<AiSeverity, number>>;
}

export type AiStatusFeature = 'BID' | 'PACING' | 'ANOMALY' | 'YIELD' | 'CREATIVE' | 'AUDIENCE' | 'FORECAST' | 'EXTERNAL';
export type AiDataFreshness = 'FRESH' | 'DELAYED' | 'STALE';

/** GET /api/ai/status. Each engine's switch and how current the reporting data is. */
export interface AiStatus {
  features: Partial<Record<AiStatusFeature, boolean>>;
  dataFreshness: AiDataFreshness;
  lastRebuiltAt?: string;
}

/** Engines whose output lands in the Recommendations tab. */
export const AI_RECOMMENDATION_FEATURES: AiStatusFeature[] = ['BID', 'PACING', 'YIELD', 'CREATIVE', 'AUDIENCE'];

export interface AiRecommendationListFilter {
  page?: number;
  size?: number;
  sortProperty?: string[];
  sortDirection?: 'ASC' | 'DESC';
  type?: AiRecommendationType;
  targetType?: AiTargetType;
  targetId?: string;
  status?: AiRecommendationStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface AiAnomalyListFilter {
  page?: number;
  size?: number;
  sortProperty?: string[];
  sortDirection?: 'ASC' | 'DESC';
  category?: AiAnomalyCategory;
  targetType?: AiTargetType;
  targetId?: string;
  severity?: AiSeverity;
  status?: AiAnomalyStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface AiAnomalyStatusRequest {
  status: AiAnomalyStatus;
  note?: string;
}

/** One hour from GET /api/ai/anomalies/{id}/series. */
export interface AiAnomalySeriesPoint {
  hour: string;
  observed: number | null;
  expectedLow: number | null;
  expectedHigh: number | null;
}

export interface AiRecommendationRejectRequest {
  reason: string;
}

export interface AiInsightMetric {
  label: string;
  value: string;
}

/** View model for the shared card. Built from a recommendation or an anomaly. */
export interface AiInsightCardModel {
  id: string;
  title: string;
  statusLabel: string;
  statusPill: string;
  badgeLabel: string | null;
  badgePill: string | null;
  sourceLabel: string | null;
  dispositionLabel: string | null;
  dispositionPill: string | null;
  observedLabel: string;
  observedValue: string;
  suggestion: string;
  rationale: string;
  supportingMetrics: AiInsightMetric[];
  dataWindow: string;
  ruleVersion: string | null;
  targetHref: string | null;
  targetLabel: string | null;
}

export interface AiInsightsListView {
  cards: AiInsightCardModel[];
  totalElements: number;
  summary: AiSummary;
  dataAsOf: string | null;
  /** Null on mock data, or when /api/ai/status could not be read. */
  status: AiStatus | null;
}

export function isAiInsightsPreview(value: string | null): value is AiInsightsPreview {
  return !!value && (AI_INSIGHTS_PREVIEWS as string[]).includes(value);
}

/** Accepts a bare array or { points | series | content }. Hour and value names vary by writer. */
export function normalizeAnomalySeries(body: unknown): AiAnomalySeriesPoint[] {
  const source = Array.isArray(body)
    ? body
    : (body && typeof body === 'object' ? seriesRows(body as Record<string, unknown>) : []);
  if (!Array.isArray(source)) {
    return [];
  }
  return source.flatMap(row => {
    if (!row || typeof row !== 'object') {
      return [];
    }
    const record = row as Record<string, unknown>;
    const hour = record.hour ?? record.timestamp ?? record.bucket ?? record.time ?? record.detectedAt;
    if (hour == null || hour === '') {
      return [];
    }
    return [{
      hour: String(hour),
      observed: asSeriesNumber(record.observed ?? record.observedValue ?? record.value),
      expectedLow: asSeriesNumber(record.expectedLow ?? record.low),
      expectedHigh: asSeriesNumber(record.expectedHigh ?? record.high),
    }];
  });
}

function seriesRows(body: Record<string, unknown>): unknown[] {
  const rows = body.points ?? body.series ?? body.content;
  return Array.isArray(rows) ? rows : [];
}

function asSeriesNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
